import { Button, Card, cn } from '@up2stay/up2promo-ui';
import { Bell, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { ListEmptyState } from '@/core/components/layout';
import {
  SETTINGS_SECTION_DESCRIPTION_CLASS,
  SETTINGS_SECTION_TITLE_CLASS,
} from '@/core/components/settings/settings-section';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import {
  useNotificationInbox,
  useNotificationMutations,
  useUnreadNotificationCount,
} from '@/core/hooks/use-notifications';

type NotificationInboxPanelProps = {
  dict: {
    inboxTitle: string;
    inboxDescription: string;
    emptyInbox: string;
    emptyUnreadInbox: string;
    markAllRead: string;
    showReadHistory: string;
    hideReadHistory: string;
  };
};

export function NotificationInboxPanel({ dict }: NotificationInboxPanelProps) {
  const { navigateShellDeepLink } = useNavigateShell();
  const [showReadHistory, setShowReadHistory] = useState(false);
  const { data: inbox, isLoading } = useNotificationInbox({
    unreadOnly: !showReadHistory,
  });
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { markRead, markAllRead } = useNotificationMutations();

  const emptyMessage = showReadHistory
    ? dict.emptyInbox
    : dict.emptyUnreadInbox;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className={SETTINGS_SECTION_TITLE_CLASS}>{dict.inboxTitle}</h2>
          <p className={SETTINGS_SECTION_DESCRIPTION_CLASS}>
            {dict.inboxDescription.replace('{count}', String(unreadCount))}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowReadHistory((current) => !current)}
          >
            {showReadHistory ? dict.hideReadHistory : dict.showReadHistory}
          </Button>
          {unreadCount > 0 ? (
            <Button variant="outline" size="sm" onClick={() => markAllRead()}>
              {dict.markAllRead}
            </Button>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !inbox?.length ? (
        <ListEmptyState
          icon={<Bell className="h-6 w-6" aria-hidden />}
          title={emptyMessage}
          action={
            !showReadHistory ? (
              <Button
                variant="ghostAccent"
                size="sm"
                onClick={() => setShowReadHistory(true)}
              >
                {dict.showReadHistory}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="min-w-0 overflow-hidden">
          <div className="divide-y divide-border">
            {inbox.map((notification) => (
              <button
                key={notification.id}
                type="button"
                className={cn(
                  'flex w-full items-start justify-between gap-3 p-4 text-left transition-colors hover:bg-[color:var(--ghost-accent-bg)]/40 sm:p-5',
                  notification.readAt && 'opacity-70',
                )}
                onClick={async () => {
                  if (!notification.readAt) {
                    await markRead(notification.id);
                  }
                  if (notification.deepLink) {
                    navigateShellDeepLink(notification.deepLink);
                  }
                }}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-semibold leading-snug text-foreground">
                    {notification.title}
                  </p>
                  <p className="mt-1 line-clamp-2 text-[13.5px] leading-relaxed text-muted-foreground">
                    {notification.body}
                  </p>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                    {new Date(notification.createdAt).toLocaleString()}
                  </p>
                </div>
                {!notification.readAt ? (
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[color:var(--brand-600)]"
                    aria-hidden
                  />
                ) : null}
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
